import {
  Alert,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Steps,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { AppState } from "src/store";

import { useApiContext } from "../../../utils";

import { createRecipeApi } from "./api";
import {
  RecipeExampleURL,
  RecipeLoadResponse,
  RecipeOutputColumn,
  RecipeParameter,
  RecipePromptSections,
  RecipeSmartRefresh,
  RunInfo,
  RunResults,
} from "./types";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PARAM_TYPES = [
  { value: "string", label: "String" },
  { value: "integer", label: "Integer" },
  { value: "list", label: "List" },
  { value: "bool", label: "Boolean" },
  { value: "dict", label: "Dict" },
  { value: "parameterized_list", label: "Parameterized List" },
];

const DEFAULT_SECTIONS: RecipePromptSections = {
  recipe_name: "",
  source: "",
  goal: "",
  example_urls: [{ url: "" }],
  output_columns: [{ name: "" }],
};

export const RecipeEditor: React.FC = () => {
  const { source, recipeId } = useParams<{ source: string; recipeId: string }>();
  const location = useLocation();
  const apiContext = useApiContext();
  const cloneFrom = new URLSearchParams(location.search).get("cloneFrom");
  const currentUserId = useSelector(({ app }: AppState) => app.coeffUserId);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [activeRunStatus, setActiveRunStatus] = useState<string | null>(null);
  const [activeRunError, setActiveRunError] = useState<string | null>(null);
  const [runResults, setRunResults] = useState<RunResults | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<boolean | null>(null);
  const [loadingRunResults, setLoadingRunResults] = useState(false);
  const [userId, setUserId] = useState(currentUserId || "");
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [isProdEnabled, setIsProdEnabled] = useState(false);
  const [smartRefreshEnabled, setSmartRefreshEnabled] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const pollIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStartTimeRef = useRef<number>(0);

  const api = useMemo(() => {
    if (!apiContext?.DEPRECATED_api) {
      return null;
    }
    return createRecipeApi(apiContext.DEPRECATED_api);
  }, [apiContext]);

  // Load existing recipe data (or clone source)
  useEffect(() => {
    if (!api) {
      return;
    }
    setLoading(true);
    const loadId = cloneFrom || recipeId;
    api
      .load(loadId)
      .then((data: RecipeLoadResponse) => {
        form.setFieldsValue({
          recipe_name: cloneFrom ? `${data.recipe_name || ""} (Copy)` : data.recipe_name || "",
          goal: data.goal || "",
          example_urls: data.example_urls?.length ? data.example_urls : [{ url: "" }],
          output_columns: data.output_columns?.length ? data.output_columns : [{ name: "" }],
          sample_data: data.sample_data,
          parameters: data.parameters,
          smart_refresh: data.smart_refresh,
          notes: data.notes || "",
          feedback: data.feedback || "",
        });
        setSmartRefreshEnabled(data.smart_refresh?.enabled || false);

        // Warn if the saved source doesn't match the URL param
        if (data.source && source && data.source !== source && !cloneFrom) {
          message.warning(
            `This recipe was saved with source "${data.source}" but you're viewing it under "${source}".`
          );
        }

        // Don't load runs when cloning — this is a new recipe
        if (!cloneFrom) {
          setRuns(data.runs || []);
          setIsPublished(data.is_published || false);
          setIsProdEnabled(data.prod_enabled || false);
        }
      })
      .catch(err => {
        console.error("Failed to load recipe:", err);
      })
      .finally(() => setLoading(false));
  }, [recipeId, api, form, cloneFrom]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
      }
    };
  }, []);

  const buildSections = useCallback((): RecipePromptSections => {
    const values = form.getFieldsValue(true);
    const sections: RecipePromptSections = {
      recipe_name: values.recipe_name || "",
      source,
      goal: values.goal || "",
      example_urls: (values.example_urls || []).filter((u: RecipeExampleURL) => u && u.url),
      output_columns: (values.output_columns || []).filter((c: RecipeOutputColumn) => c && c.name),
    };

    if (values.sample_data?.length) {
      sections.sample_data = values.sample_data;
    }
    if (values.parameters?.length) {
      sections.parameters = values.parameters.filter((p: RecipeParameter) => p && p.key);
    }
    if (smartRefreshEnabled) {
      sections.smart_refresh = {
        enabled: true,
        unique_columns: values.smart_refresh?.unique_columns || [],
        watermark_behavior: values.smart_refresh?.watermark_behavior,
      };
    }
    if (values.notes) {
      sections.notes = values.notes;
    }
    if (values.feedback) {
      sections.feedback = values.feedback;
    }

    return sections;
  }, [form, source, smartRefreshEnabled]);

  const handleSave = useCallback(async () => {
    if (!api) {
      return;
    }
    setSaving(true);
    try {
      const sections = buildSections();
      await api.save(recipeId, sections);
      message.success("Recipe saved");
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to save recipe");
    } finally {
      setSaving(false);
    }
  }, [api, recipeId, buildSections]);

  const startPolling = useCallback(
    (runId: string) => {
      if (!api) {
        return;
      }
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
      }
      pollStartTimeRef.current = Date.now();

      const poll = async () => {
        try {
          const status = await api.pollStatus(recipeId, runId);
          setActiveRunStatus(status.status);
          setActiveRunError(status.error || null);

          if (status.status === "success" || status.status === "failure") {
            pollIntervalRef.current = null;
            if (status.status === "success") {
              try {
                const results = await api.getResults(recipeId, runId);
                setRunResults(results);
              } catch {
                // Results may not be ready yet
              }
            }
            return; // Stop polling
          }
        } catch {
          // Polling error, will retry
        }

        // Adaptive interval: 3s for first minute, then 10s
        const elapsed = Date.now() - pollStartTimeRef.current;
        const interval = elapsed < 60000 ? 3000 : 10000;
        pollIntervalRef.current = setTimeout(poll, interval);
      };

      // Start first poll after initial delay
      pollIntervalRef.current = setTimeout(poll, 3000);
    },
    [api, recipeId]
  );

  const handleSaveAndRun = useCallback(async () => {
    if (!api || !userId) {
      message.error("Please enter a User ID and check connection first");
      return;
    }
    setSaving(true);
    try {
      // Save first
      const sections = buildSections();
      await api.save(recipeId, sections);

      // Then run
      const result = await api.run(recipeId, userId);
      setActiveRunId(result.run_id);
      setActiveRunStatus(result.status);
      setActiveRunError(null);
      setRunResults(null);
      message.success("Recipe saved and run started");

      // Start polling
      startPolling(result.run_id);
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to save and run");
    } finally {
      setSaving(false);
    }
  }, [api, userId, recipeId, buildSections, startPolling]);

  const handleCheckConnection = useCallback(async () => {
    if (!api) {
      return;
    }
    try {
      const result = await api.checkConnection();
      setConnectionStatus(result.is_connected);
      if (result.is_connected) {
        message.success("Browser extension connected");
      } else {
        message.warning(
          "Browser extension not connected. Open the Coeff extension in your browser."
        );
      }
    } catch {
      setConnectionStatus(false);
      message.error("Failed to check connection");
    }
  }, [api]);

  const handlePublish = useCallback(async () => {
    if (!api || !activeRunId) {
      console.error("handlePublish called without api or activeRunId");
      return;
    }
    setPublishing(true);
    try {
      const result = await api.publish(recipeId, activeRunId);
      setPublishModalVisible(false);

      // Update local state to reflect the publish
      setIsPublished(true);
      setIsProdEnabled(false); // publish always demotes from prod

      if (result.was_prod_enabled) {
        Modal.warning({
          title: "Published (Production Demoted)",
          content: (
            <div>
              <p>
                This recipe was previously live in production. It has been re-published as
                dev/staging only. You will need to explicitly promote it to production again from
                the recipe list.
              </p>
              <pre style={{ maxHeight: 200, overflow: "auto" }}>
                {JSON.stringify(result.manifest_entry, null, 2)}
              </pre>
            </div>
          ),
          width: 600,
        });
      } else if (result.is_republish) {
        message.success("Recipe re-published to dev/staging");
      } else {
        message.success("Recipe published to dev/staging");
      }
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to publish");
    } finally {
      setPublishing(false);
    }
  }, [api, activeRunId, recipeId]);

  const handleViewRunResults = useCallback(
    async (runId: string) => {
      if (!api) {
        return;
      }
      // Stop any active polling from a previous Save & Run
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setActiveRunId(runId);
      setRunResults(null);
      setLoadingRunResults(true);
      try {
        const status = await api.pollStatus(recipeId, runId);
        setActiveRunStatus(status.status);
        setActiveRunError(status.error || null);

        if (status.status === "success") {
          const results = await api.getResults(recipeId, runId);
          setRunResults(results);
        }
      } catch {
        message.error("Failed to load run details");
      } finally {
        setLoadingRunResults(false);
      }
    },
    [api, recipeId]
  );

  const applyRestoredSections = useCallback(
    (data: RecipeLoadResponse) => {
      form.setFieldsValue({
        recipe_name: data.recipe_name || "",
        goal: data.goal || "",
        example_urls: data.example_urls?.length ? data.example_urls : [{ url: "" }],
        output_columns: data.output_columns?.length ? data.output_columns : [{ name: "" }],
        sample_data: data.sample_data,
        parameters: data.parameters,
        smart_refresh: data.smart_refresh,
        notes: data.notes || "",
        feedback: data.feedback || "",
      });
      setSmartRefreshEnabled(data.smart_refresh?.enabled || false);
    },
    [form]
  );

  const handleRestoreFromPublished = useCallback(() => {
    if (!api) {
      return;
    }
    Modal.confirm({
      title: "Restore from Published Version",
      content:
        "This will overwrite your current draft with the version that was last published. Continue?",
      onOk: async () => {
        setRestoring(true);
        try {
          const data = await api.restoreFromPublished(recipeId);
          applyRestoredSections(data);
          message.success("Restored from published version");
        } catch (error: unknown) {
          const err = error as { error?: { message?: string } };
          message.error(err?.error?.message || "Failed to restore from published version");
        } finally {
          setRestoring(false);
        }
      },
    });
  }, [api, recipeId, applyRestoredSections]);

  const handleRestoreFromRun = useCallback(
    (runId: string) => {
      if (!api) {
        return;
      }
      Modal.confirm({
        title: "Restore from Run",
        content: `This will overwrite your current draft with the form data from run ${runId}. Continue?`,
        onOk: async () => {
          setRestoring(true);
          try {
            const data = await api.restoreFromRun(recipeId, runId);
            applyRestoredSections(data);
            message.success(`Restored from run ${runId}`);
          } catch (error: unknown) {
            const err = error as { error?: { message?: string } };
            message.error(err?.error?.message || "Failed to restore from run");
          } finally {
            setRestoring(false);
          }
        },
      });
    },
    [api, recipeId, applyRestoredSections]
  );

  const outputColumns = Form.useWatch("output_columns", form) || [];
  const columnNames = outputColumns
    .filter((c: RecipeOutputColumn | undefined) => c?.name)
    .map((c: RecipeOutputColumn) => c.name);

  const getRunStepStatus = () => {
    if (!activeRunStatus || activeRunStatus === "unknown") {
      return 0;
    }
    if (activeRunStatus === "pending") {
      return 0;
    }
    if (activeRunStatus === "in_progress") {
      return 1;
    }
    if (activeRunStatus === "success") {
      return 2;
    }
    if (activeRunStatus === "failure") {
      return 2;
    }
    return 0;
  };

  // Parse CSV preview into table data
  const csvPreviewData = useMemo(() => {
    const empty = {
      columns: [] as { title: string; dataIndex: string; key: string }[],
      data: [] as Record<string, string>[],
    };

    // Prefer server-parsed CSV data
    if (runResults?.export_csv_headers?.length) {
      const headers = runResults.export_csv_headers;
      const columns = headers.map(h => ({ title: h, dataIndex: h, key: h }));
      const data = (runResults.export_csv_rows || []).map((values, idx) => {
        const row: Record<string, string> = { key: String(idx) };
        headers.forEach((h, i) => {
          row[h] = values[i] || "";
        });
        return row;
      });
      return { columns, data };
    }

    // Fallback to old CSV preview string. Uses a basic CSV parser that handles
    // quoted fields (the server-parsed path above is preferred and handles all cases).
    if (!runResults?.export_csv_preview) {
      return empty;
    }
    const lines = runResults.export_csv_preview.split("\n").filter(l => l.trim());
    if (lines.length === 0) {
      return empty;
    }

    const parseCsvLine = (line: string): string[] => {
      const result: string[] = [];
      let current = "";
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
          if (ch === '"' && line[i + 1] === '"') {
            current += '"';
            i++; // skip escaped quote
          } else if (ch === '"') {
            inQuotes = false;
          } else {
            current += ch;
          }
        } else {
          if (ch === '"') {
            inQuotes = true;
          } else if (ch === ",") {
            result.push(current.trim());
            current = "";
          } else {
            current += ch;
          }
        }
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseCsvLine(lines[0]);
    const columns = headers.map(h => ({ title: h, dataIndex: h, key: h }));
    const data = lines.slice(1).map((line, idx) => {
      const values = parseCsvLine(line);
      const row: Record<string, string> = { key: String(idx) };
      headers.forEach((h, i) => {
        row[h] = values[i] || "";
      });
      return row;
    });
    return { columns, data };
  }, [runResults?.export_csv_preview, runResults?.export_csv_headers, runResults?.export_csv_rows]);

  const runHistoryColumns = [
    { title: "Run ID", dataIndex: "run_id", key: "run_id", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          success: "green",
          failure: "red",
          pending: "orange",
          in_progress: "blue",
        };
        return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: RunInfo) => (
        <Space>
          <Button
            type="link"
            loading={loadingRunResults && activeRunId === record.run_id}
            onClick={() => handleViewRunResults(record.run_id)}
          >
            View
          </Button>
          <Button
            type="link"
            disabled={restoring}
            onClick={() => handleRestoreFromRun(record.run_id)}
          >
            Restore
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) {
    return <div style={{ padding: 24 }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <Title level={3}>{`Edit Recipe: ${recipeId}`}</Title>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...DEFAULT_SECTIONS,
          source,
        }}
      >
        <Collapse defaultActiveKey={["basic", "urls", "columns"]} style={{ marginBottom: 24 }}>
          {/* Basic Info */}
          <Panel header="Basic Info" key="basic">
            <Form.Item label="Recipe Name" name="recipe_name" rules={[{ required: true }]}>
              <Input placeholder="e.g. Post Reactions" />
            </Form.Item>
            <Form.Item label="Goal" name="goal" rules={[{ required: true }]}>
              <Input.TextArea rows={4} placeholder="Describe what this recipe should do..." />
            </Form.Item>
          </Panel>

          {/* Example URLs */}
          <Panel header="Example URLs" key="urls">
            <Form.List name="example_urls">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(field => (
                    <Space
                      key={field.key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="start"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "url"]}
                        rules={[{ required: true, message: "URL is required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="https://www.linkedin.com/..." style={{ width: 400 }} />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "description"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Description (optional)" style={{ width: 250 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      )}
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add({ url: "", description: "" })}>
                    Add URL
                  </Button>
                </>
              )}
            </Form.List>
          </Panel>

          {/* Output Columns */}
          <Panel header="Output Columns" key="columns">
            <Form.List name="output_columns">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(field => (
                    <Space
                      key={field.key}
                      style={{ display: "flex", marginBottom: 8 }}
                      align="start"
                    >
                      <Form.Item
                        {...field}
                        name={[field.name, "name"]}
                        rules={[{ required: true, message: "Column name is required" }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Column name" style={{ width: 250 }} />
                      </Form.Item>
                      <Form.Item
                        {...field}
                        name={[field.name, "description"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder="Description (optional)" style={{ width: 300 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      )}
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add({ name: "", description: "" })}>
                    Add Column
                  </Button>
                </>
              )}
            </Form.List>
          </Panel>

          {/* Parameters (optional) */}
          <Panel header="Parameters (optional)" key="params">
            <Form.List name="parameters">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(field => (
                    <Card key={field.key} size="small" style={{ marginBottom: 8 }}>
                      <Space wrap>
                        <Form.Item
                          {...field}
                          name={[field.name, "key"]}
                          label="Key"
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="param_name" style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "type"]}
                          label="Type"
                          initialValue="string"
                          style={{ marginBottom: 4 }}
                        >
                          <Select options={PARAM_TYPES} style={{ width: 160 }} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "required"]}
                          label="Required"
                          valuePropName="checked"
                          style={{ marginBottom: 4 }}
                        >
                          <Switch />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "default"]}
                          label="Default"
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="default value" style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, "description"]}
                          label="Description"
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="parameter description" style={{ width: 200 }} />
                        </Form.Item>
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      </Space>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() =>
                      add({
                        key: "",
                        type: "string",
                        required: false,
                        default: "",
                        description: "",
                      })
                    }
                  >
                    Add Parameter
                  </Button>
                </>
              )}
            </Form.List>
          </Panel>

          {/* Smart Refresh (optional) */}
          <Panel header="Smart Refresh (optional)" key="smartrefresh">
            <Form.Item label="Enable Smart Refresh">
              <Switch checked={smartRefreshEnabled} onChange={setSmartRefreshEnabled} />
            </Form.Item>
            {smartRefreshEnabled && (
              <>
                <Form.Item
                  label="Unique Columns (for deduplication)"
                  name={["smart_refresh", "unique_columns"]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select columns"
                    options={columnNames.map((n: string) => ({ value: n, label: n }))}
                  />
                </Form.Item>
                <Form.Item
                  label="Watermark Behavior"
                  name={["smart_refresh", "watermark_behavior"]}
                >
                  <Input.TextArea rows={2} placeholder="Describe watermark behavior (optional)" />
                </Form.Item>
              </>
            )}
          </Panel>

          {/* Notes (optional) */}
          <Panel header="Notes (optional)" key="notes">
            <Form.Item name="notes">
              <Input.TextArea rows={3} placeholder="Additional instructions or notes..." />
            </Form.Item>
          </Panel>

          {/* Feedback (for iteration) */}
          {runs.length > 0 && (
            <Panel header="Feedback (for iteration)" key="feedback">
              <Form.Item name="feedback">
                <Input.TextArea
                  rows={4}
                  placeholder="Describe issues from the previous run to fix in the next iteration..."
                />
              </Form.Item>
            </Panel>
          )}
        </Collapse>

        {/* Connection & Actions */}
        <Card title="Run Configuration" style={{ marginBottom: 24 }}>
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="User ID"
              value={userId}
              onChange={e => {
                setUserId(e.target.value);
                setConnectionStatus(null);
              }}
              style={{ width: 300 }}
            />
            <Button onClick={handleCheckConnection}>Check Connection</Button>
            {connectionStatus !== null && (
              <Tag color={connectionStatus ? "green" : "red"}>
                {connectionStatus ? "Connected" : "Not Connected"}
              </Tag>
            )}
          </Space>

          <div>
            <Space>
              <Button onClick={handleSave} loading={saving}>
                Save
              </Button>
              <Button
                type="primary"
                onClick={handleSaveAndRun}
                loading={saving}
                disabled={!connectionStatus}
              >
                Save & Run
              </Button>
              {isPublished && (
                <Button onClick={handleRestoreFromPublished} loading={restoring}>
                  Restore from Published
                </Button>
              )}
            </Space>
          </div>
        </Card>

        {/* Run Status */}
        {activeRunId && (
          <Card title={`Run: ${activeRunId}`} style={{ marginBottom: 24 }}>
            <Steps
              current={getRunStepStatus()}
              status={activeRunStatus === "failure" ? "error" : undefined}
              items={[
                { title: "Pending" },
                { title: "In Progress" },
                { title: activeRunStatus === "failure" ? "Failed" : "Complete" },
              ]}
              style={{ marginBottom: 16 }}
            />

            {activeRunError && (
              <Alert
                type="error"
                message="Run Failed"
                description={activeRunError}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Results */}
            {runResults && (
              <Collapse>
                {runResults.metadata && (
                  <Panel header="Metadata" key="metadata">
                    <pre style={{ maxHeight: 300, overflow: "auto" }}>
                      {JSON.stringify(runResults.metadata, null, 2)}
                    </pre>
                  </Panel>
                )}
                {csvPreviewData.columns.length > 0 && (
                  <Panel header="CSV Preview" key="csv">
                    <Table
                      dataSource={csvPreviewData.data}
                      columns={csvPreviewData.columns}
                      scroll={{ x: true }}
                      pagination={false}
                      size="small"
                    />
                  </Panel>
                )}
                <Panel header="S3 Path" key="s3">
                  <Text copyable>{runResults.s3_output_path}</Text>
                </Panel>
              </Collapse>
            )}

            {activeRunStatus === "success" && (
              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={() => setPublishModalVisible(true)}>
                  Publish Recipe
                </Button>
              </div>
            )}
          </Card>
        )}

        {/* Run History */}
        {runs.length > 0 && (
          <Card title="Run History" style={{ marginBottom: 24 }}>
            <Table
              dataSource={runs}
              columns={runHistoryColumns}
              rowKey="run_id"
              pagination={false}
              size="small"
            />
          </Card>
        )}
      </Form>

      {/* Publish Modal */}
      <Modal
        title="Publish Recipe to Dev/Staging"
        open={publishModalVisible}
        onOk={handlePublish}
        onCancel={() => setPublishModalVisible(false)}
        confirmLoading={publishing}
        okText={isPublished ? "Re-publish to Dev/Staging" : "Publish to Dev/Staging"}
        okButtonProps={isProdEnabled ? { danger: true } : undefined}
      >
        {isProdEnabled && (
          <Alert
            type="error"
            showIcon
            message="This recipe is currently live in production"
            description={
              "Re-publishing will overwrite the current version and demote it from production. " +
              "You will need to promote it to production again after testing. " +
              "Consider cloning the recipe instead if you want to keep the current production version."
            }
            style={{ marginBottom: 16 }}
          />
        )}
        {isPublished && !isProdEnabled && (
          <Alert
            type="warning"
            showIcon
            message="This recipe is already published"
            description="Re-publishing will overwrite the current dev/staging version."
            style={{ marginBottom: 16 }}
          />
        )}
        <div style={{ marginBottom: 16 }}>
          This will publish the recipe to the manifest, making it available in dev/staging.
        </div>
        <Alert
          type="info"
          showIcon
          message="Production promotion is separate"
          description="To enable in production, use the 'Promote to Prod' button on the recipe list page after testing in dev/staging."
        />
      </Modal>
    </div>
  );
};
